import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller } from 'react-hook-form';
import { CustomButton } from '../components/CustomButton';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { AppModal, ModalType } from '../components/AppModal';
import { ScreenHeader } from '../components/ScreenHeader';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { removeSpaces } from '../utils/helpers';

interface ChangePasswordFormData {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
    otp: string;
}

const ChangePasswordScreen = ({ navigation }: any) => {
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [maskedPhone, setMaskedPhone] = useState('');
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
    }>({ visible: false, type: 'error', title: '', message: '' });

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ChangePasswordFormData>({
        defaultValues: {
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
            otp: '',
        },
        mode: 'onBlur',
    });

    const newPassword = watch('newPassword');

    const handleSendOTP = useCallback(async () => {
        setLoading(true);
        try {
            const result = await ApiService.sendChangePasswordOTP();

            if (result.phoneNumber) {
                setMaskedPhone(result.phoneNumber);
                setOtpSent(true);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'OTP Sent',
                    message: `Verification code has been sent to your WhatsApp number ending with ${result.phoneNumber.slice(-4)}`,
                });
            } else {
                setModal({
                    visible: true,
                    type: 'error',
                    title: 'Error',
                    message: 'Failed to send OTP. Please try again.',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: error.message || 'Failed to send OTP',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const onSubmit = useCallback(async (data: ChangePasswordFormData) => {
        if (!otpSent) {
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Please send verification code first',
            });
            return;
        }

        setLoading(true);
        try {
            const result = await ApiService.verifyChangePasswordOTP(
                data.oldPassword,
                data.newPassword,
                data.otp
            );

            if (result.success) {
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Password changed successfully',
                });
                setTimeout(() => {
                    navigation.goBack();
                }, 1500);
            } else {
                setModal({
                    visible: true,
                    type: 'error',
                    title: 'Error',
                    message: result.message || 'Failed to change password',
                });
            }
        } catch (error: any) {
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: error.message || 'An unexpected error occurred',
            });
        } finally {
            setLoading(false);
        }
    }, [otpSent, navigation]);

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader title="Change Password" showBackButton={true} navigation={navigation} />

            <KeyboardAwareScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.formContainer}>
                    {/* Old Password */}
                    <Controller
                        control={control}
                        name="oldPassword"
                        rules={{
                            required: 'Current password is required',
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FloatingLabelInput
                                label="Old Password"
                                value={value}
                                onChangeText={(text) => onChange(removeSpaces(text))}
                                onBlur={onBlur}
                                error={errors.oldPassword?.message}
                                secureTextEntry={!showOldPassword}
                                rightIcon={
                                    <TouchableOpacity onPress={() => setShowOldPassword(!showOldPassword)}>
                                        <Icon
                                            name={showOldPassword ? 'eye-off' : 'eye'}
                                            size={20}
                                            color={theme.colors.textSecondary}
                                        />
                                    </TouchableOpacity>
                                }
                            />
                        )}
                    />

                    {/* New Password */}
                    <Controller
                        control={control}
                        name="newPassword"
                        rules={{
                            required: 'New password is required',
                            minLength: {
                                value: 6,
                                message: 'Password must be at least 6 characters',
                            },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FloatingLabelInput
                                label="New Password"
                                value={value}
                                onChangeText={(text) => onChange(removeSpaces(text))}
                                onBlur={onBlur}
                                error={errors.newPassword?.message}
                                secureTextEntry={!showNewPassword}
                                rightIcon={
                                    <TouchableOpacity onPress={() => setShowNewPassword(!showNewPassword)}>
                                        <Icon
                                            name={showNewPassword ? 'eye-off' : 'eye'}
                                            size={20}
                                            color={theme.colors.textSecondary}
                                        />
                                    </TouchableOpacity>
                                }
                            />
                        )}
                    />

                    {/* Confirm Password */}
                    <Controller
                        control={control}
                        name="confirmPassword"
                        rules={{
                            required: 'Please confirm your password',
                            validate: (value) =>
                                value === newPassword || 'Passwords do not match',
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <FloatingLabelInput
                                label="Confirm Password"
                                value={value}
                                onChangeText={(text) => onChange(removeSpaces(text))}
                                onBlur={onBlur}
                                error={errors.confirmPassword?.message}
                                secureTextEntry={!showConfirmPassword}
                                rightIcon={
                                    <TouchableOpacity onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                                        <Icon
                                            name={showConfirmPassword ? 'eye-off' : 'eye'}
                                            size={20}
                                            color={theme.colors.textSecondary}
                                        />
                                    </TouchableOpacity>
                                }
                            />
                        )}
                    />

                    {/* Verification Code Row */}
                    <View style={styles.otpRow}>
                        <View style={styles.otpInputContainer}>
                            <Controller
                                control={control}
                                name="otp"
                                rules={{
                                    required: otpSent ? 'Verification code is required' : false,
                                    pattern: {
                                        value: /^\d{6}$/,
                                        message: 'Enter valid 6-digit code',
                                    },
                                }}
                                render={({ field: { onChange, onBlur, value } }) => (
                                    <FloatingLabelInput
                                        label="Verification Code"
                                        value={value}
                                        onChangeText={onChange}
                                        onBlur={onBlur}
                                        error={errors.otp?.message}
                                        keyboardType="number-pad"
                                        maxLength={6}
                                        placeholder="6 digit code"
                                    />
                                )}
                            />
                        </View>

                        <TouchableOpacity onPress={handleSendOTP} style={styles.sendOtpLink} disabled={loading}>
                            <Text style={styles.sendOtpText}>
                                {otpSent ? "Resend Verification Code" : "Send Verification Code"}
                            </Text>
                        </TouchableOpacity>
                    </View>

                    {otpSent && maskedPhone && (
                        <View style={styles.otpInfo}>
                            <Icon name="information" size={16} color={theme.colors.info} />
                            <Text style={styles.otpInfoText}>
                                Code sent to ****{maskedPhone.slice(-4)}
                            </Text>
                        </View>
                    )}
                </View>

                {/* Submit Button */}
                <CustomButton
                    title="CHANGE PASSWORD"
                    onPress={handleSubmit(onSubmit)}
                    loading={loading}
                    style={styles.submitButton}
                />

                {/* Close Button */}
                {/* <CustomButton
                    title="CLOSE"
                    onPress={() => navigation.goBack()}
                    style={styles.closeButton}
                    variant="primary"
                /> */}

            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollContent: {
        flexGrow: 1,
        padding: theme.spacing.lg,
    },
    formContainer: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.md,
        marginBottom: theme.spacing.lg,
    },
    otpRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.md,
        marginTop: theme.spacing.xs,
    },
    otpInputContainer: {
        flex: 0.5,
    },
    sendOtpLink: {
        flex: 0.5,
        justifyContent: 'center',
        marginTop: 4,
    },
    sendOtpText: {
        ...theme.typography.button,
        color: theme.colors.primary,
        fontWeight: '700',
        fontSize: 14,
        textDecorationLine: 'underline',
    },
    otpInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.info + '10',
        padding: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        marginTop: theme.spacing.md,
    },
    otpInfoText: {
        ...theme.typography.caption,
        color: theme.colors.info,
        marginLeft: theme.spacing.xs,
    },
    submitButton: {
        marginTop: theme.spacing.sm,
        backgroundColor: '#107ea0ff', // Greenish button
        borderRadius: 4,
    },
    closeButton: {
        marginTop: theme.spacing.md,
        backgroundColor: '#212121', // Dark button
        borderRadius: 4,
    },
});

export default ChangePasswordScreen;
